import {ChangeDetectorRef, Component, HostBinding, NgZone, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {AppStore} from "./store/appStore";
import {Store} from "@ngrx/store";
import {ChangeAppInitStatus} from "./store/status/store/status.actions";
import {APP_INIT_STATES} from "./store/status/status";
import {PushNotificationService} from "./io/push-notification.service";
import {DeviceService} from "./io/device/device.service";
import {ConfigService} from "./config/config.service";
import {Capacitor} from "@capacitor/core";
import {Device} from "@capacitor/device";
import {ViewService} from "./io/entity/view.service";
import {AccessService} from "./services/access.service";
import {BiometricAccessService} from "./services/biometric-access.service";
import {NavigationEnd, Router} from "@angular/router";
import {App, URLOpenListenerEvent} from "@capacitor/app";
import {environment} from "../environments/environment";
import {Environments} from "../environments/environments";
import {VersionService} from "./services/version.service";
import {StatusBar, Style} from '@capacitor/status-bar';

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  @HostBinding("style.--safe-area-top") safeAreaTop: string = "";
  @HostBinding("style.display") display: string = "block !important";

  public biometricCheck: boolean;
  public isSecuredPath = false;


  constructor(
    private _store: Store<AppStore>,
    private _pushNotificationService: PushNotificationService,
    private _deviceService: DeviceService,
    private _configService: ConfigService,
    private _viewService: ViewService,
    private _accessService: AccessService,
    private _biometricAccessService: BiometricAccessService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private zone: NgZone,
    private _versionService: VersionService
  ) {
    this._accessService.loadAndProcessAccessList(true);

    this._store.dispatch(
      ChangeAppInitStatus({ state: APP_INIT_STATES.DEFAULT })
    );

    this.info().then((di) => {
      switch (Capacitor.getPlatform()) {
        case "web":
          this.safeAreaTop = "0px";
          break;
        // No spacer on Android: Capacitor 8 already insets the WebView past the status bar
        // (SystemBars pads it on Android 15+, the system lays it out below the bar before that),
        // so the 30px this used to reserve landed on top of that inset as a visible gap.
        case "android":
          this.safeAreaTop = "0px";
          break;
        case "ios":
          this.safeAreaTop = this.hasDynamicIsland(di.model) ? "45px" : "30px";
          break;
      }
    });


    // @capacitor/status-bar has no web implementation, so on the browser every
    // call rejects with '"StatusBar" plugin is not implemented on web'. Without
    // the guard that unhandled rejection is reported as a crash on every web
    // page load, including the public /self-service/* routes.
    if (Capacitor.isPluginAvailable("StatusBar")) {
      StatusBar.setStyle({ style: Style.Light }).catch((error) => {
        console.warn("Could not set the status bar style", error);
      });
    }


  }

  public async info() {
    return await Device.getInfo();
  }

  /*
   * Whether the device has a Dynamic Island rather than a notch, which needs noticeably more room
   * at the top of the screen.
   *
   * Device.getInfo().model is the hardware identifier from sysctl hw.machine, so it always carries
   * a variant: "iPhone17,3", never a bare "iPhone17". Comparing whole strings against generation
   * prefixes therefore matched nothing, which is why every iPhone 15 and newer was still being
   * treated as a notch device.
   *
   * Matching on the generation instead, and defaulting unknown newer ones to true: every iPhone
   * since the 15 has an Island, so only the known exceptions need listing and the check does not
   * go stale with the next release. Non-iPhone identifiers (iPad, "Simulator") fail the pattern
   * and fall through to false.
   */
  private hasDynamicIsland(model: string): boolean {
    const identifier = /^iPhone(\d+),(\d+)$/.exec(model);
    if (!identifier) return false;

    const generation = Number(identifier[1]);
    const variant = Number(identifier[2]);

    // The Island arrived with the 14 Pro / Pro Max; the plain 14 and 14 Plus (iPhone14,7 and
    // iPhone14,8) still have a notch, as does everything older.
    if (generation < 15) return false;
    if (generation === 15) return variant >= 2;
    // The 16e (iPhone17,5) went back to a notch, unlike the rest of the 16 generation.
    if (generation === 17) return variant !== 5;

    return true;
  }

  ngOnInit() {
    this._biometricAccessService.biometryCheckActive$.subscribe((active) => {
      this.biometricCheck = active;
      this._changeDetectorRef.detectChanges();
    });

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isSecuredPath =
          this._biometricAccessService.isBiometricSecuredPath(
            event.urlAfterRedirects
          );
        this._changeDetectorRef.detectChanges();
      }
    });

    App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        if (event.url.indexOf("at.liechtenecker") > -1) {
          return;
        }

        const slug = event.url
          .split(
            environment.deployment === Environments.production ? ".at" : ".dev"
          )
          .pop();

        console.log(environment.deployment, slug, event.url);
        if (slug) {
          this._router.navigateByUrl(slug).catch((reason) => {
            console.error("Could not route to slug", slug, reason);
            this._router.navigateByUrl("/app/dashboard");
          });
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }
}
